// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import HighChartGraph, { ApiHandler as HC, HighChartOptions } from 'wdna-highchart';

import { emptyData, getDateFormat } from 'wdna-components/lib/helpers/highchart';

import pressets, { CARDINALS } from './pressets';

interface Props {
  title: string;
  data: any[];
}

type State = unknown;

export default class ChartWindRose extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
    };
  }

  public getOptions = (graphInput: any = {}): any => {
    const { title } = this.props;
    const { axisX } = getDateFormat(graphInput?.groupDateTime);

    const options: HighChartOptions = _.cloneDeep(pressets.spline2);
    if (options.xAxis?.[0]) {
      options.xAxis[0].tickmarkPlacement = 'on';
      options.xAxis[0].labels.rotation = 0;
      options.xAxis[0].type = 'categories';
      options.xAxis[0].categories = CARDINALS;
    }

    const hc: HC = new HC(options);
    hc.setXAxisFormat(axisX);
    hc.setOption('chart.polar', true);
    hc.setOption('title.text', `<b>${title}</b>`);
    hc.setOption('plotOptions.series.pointPlacement', 'on');
    hc.setOption('plotOptions.series.stacking', 'normal');
    hc.setOption('exporting.enabled', true);

    if (title === 'Accumulated Wind Direction') {
      hc.setOption('chart.height', '40%');
    }

    return options;
  };

  public degToCompass = (registers?: any[][]): number[][] => {
    const result: number[][] = [
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    ];

    if (registers && registers.length > 0) {
      for (let j: number = 1; j < 17; j++) {
        result[0][j - 1] = registers[j][1]; // 0 - 3
        result[1][j - 1] = registers[j][2]; // 3 - 6
        result[2][j - 1] = registers[j][3]; // 6 - 10
        result[3][j - 1] = registers[j][4]; // 10 - 13
        result[4][j - 1] = registers[j][5]; // 13 - 16
        result[5][j - 1] = registers[j][6]; // 16 - 32
        result[6][j - 1] = registers[j][7]; // > 32
      }
    }

    return result;
  };

  public getStyle = (): React.CSSProperties => {
    let style: React.CSSProperties = {};
    if (this.props.title === 'Accumulated Wind-Dir') {
      style = { height: 'calc(24vh)', minHeight: '100px', maxHeight: '250px' };
    } else {
      style = { height: 'calc(55vh)', minHeight: '350px' };
    }
    return style;
  };

  public render(): JSX.Element {
    const { data } = this.props;
    const options: any = this.getOptions();

    const compass: number[][] = this.degToCompass(data);
    const dgraph: any[] = [
      {
        name: '0 -3 km/h',
        data: compass[0],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '3 -6 km/h',
        data: compass[1],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '6 -10 km/h',
        data: compass[2],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '10 -13 km/h',
        data: compass[3],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '13 -16 km/h',
        data: compass[4],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '16 -32 km/h',
        data: compass[5],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
      {
        name: '> 32 km/h',
        data: compass[6],
        type: 'column',
        yAxis: 0,
        zIndex: 2,
      },
    ];

    const style: React.CSSProperties = this.getStyle();
    return (
      <div style={style}>
        <HighChartGraph data={dgraph || emptyData} options={options} settings />
      </div>
    );
  }
}
